// Per-weapon upgrade levels (Don's direction, 16.36): keeps the fixed
// 96-ship roster (16.16) exactly as locked -- this doesn't reintroduce a
// fitting system, it's a small Armory-only add-on. "Each weapon module on
// each ship will track its current level... spend a little credits to
// increase each of the three weapons from lvl 1 to 2 to 3... resets when
// the ship is destroyed." The three weapons are the Expedition View's own
// real combat Hardpoints (expeditionScreen.ts's WEAPON_SHELLS/WEAPON_DPS)
// -- Laser Turret/Auto-Turret/Missile Turret -- not an invented separate
// slot abstraction. Mining Laser has no upgrade levels; it's an
// extraction tool, not a weapon.
export type WeaponKey = "laser" | "auto" | "missile";
export const WEAPON_KEYS: WeaponKey[] = ["laser", "auto", "missile"];
export const MAX_WEAPON_LEVEL = 3;

// Illustrative "a little credits" placeholder curve -- cost to reach the
// given level from the one just below it. No number is locked anywhere
// in the design doc for a weapon-upgrade cost.
export const WEAPON_UPGRADE_COST_CR: Record<number, number> = {
  2: 1500,
  3: 3500
};

// Illustrative DPS bonus per level -- level 1 leaves the weapon's base
// WEAPON_DPS value unchanged; level 3 is +35%. No curve is locked
// anywhere either.
export const WEAPON_LEVEL_MULTIPLIER: Record<number, number> = {
  1: 1.0,
  2: 1.15,
  3: 1.35
};

// Keyed by ship INSTANCE id (state.currentShipInstanceId/FleetShip.id),
// never by ship name/variant -- two identically-named ships in the same
// fleet must be able to hold different upgrade levels. An instance with
// no entry (or a weapon with no entry within it) is level 1 by
// definition -- no need to pre-populate every ship/weapon combination.
export type WeaponUpgrades = Record<string, Partial<Record<WeaponKey, number>>>;

export function weaponLevel(upgrades: WeaponUpgrades, instanceId: string, weapon: WeaponKey): number {
  return upgrades[instanceId]?.[weapon] ?? 1;
}

export function weaponMultiplier(upgrades: WeaponUpgrades, instanceId: string, weapon: WeaponKey): number {
  return WEAPON_LEVEL_MULTIPLIER[weaponLevel(upgrades, instanceId, weapon)];
}

export interface UpgradeResult {
  upgrades: WeaponUpgrades;
  cost: number;
}

// Pure -- returns null if already at MAX_WEAPON_LEVEL (nothing left to
// buy). The caller (Armory UI) is responsible for checking/deducting
// credits before applying the returned upgrades, the same division of
// responsibility depositMining/transferToBase already use.
export function upgradeWeapon(upgrades: WeaponUpgrades, instanceId: string, weapon: WeaponKey): UpgradeResult | null {
  const current = weaponLevel(upgrades, instanceId, weapon);
  if (current >= MAX_WEAPON_LEVEL) {
    return null;
  }
  const nextLevel = current + 1;
  const cost = WEAPON_UPGRADE_COST_CR[nextLevel];
  const next: WeaponUpgrades = {
    ...upgrades,
    [instanceId]: { ...upgrades[instanceId], [weapon]: nextLevel }
  };
  return { upgrades: next, cost };
}

// Called when a ship instance is destroyed ("it gets reset when the ship
// is destroyed") -- drops that instance's entry entirely, so a freshly-
// flown replacement (or the same ship respawned) starts back at level 1
// on every weapon rather than carrying a stale, now-meaningless levels
// object forward.
export function resetShipUpgrades(upgrades: WeaponUpgrades, instanceId: string): WeaponUpgrades {
  if (!(instanceId in upgrades)) {
    return upgrades;
  }
  const next = { ...upgrades };
  delete next[instanceId];
  return next;
}
